import React, { useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileText, Info, Loader2, UploadCloud, X } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { cn } from '../../components/ui/utils';
import { ArchiveAttachment, TERMINATION_REASONS } from '../../types/personArchive';
import { toSydneyDate } from '../../utils/helpers';

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

interface ArchiveTerminationCardProps {
  personFirstName: string;
  personName: string;
  terminationDate: string;
  terminationReason: string;
  onTerminationDateChange: (value: string) => void;
  onTerminationReasonChange: (value: string) => void;
  attachment: ArchiveAttachment | null;
  attachmentUrl?: string;
  onUpload: (file: File) => void;
  onRemoveAttachment: () => void;
  uploading: boolean;
  unresolvedItems: number;
  unresolvedCategories: number;
}

/**
 * Termination details + the status banner + the optional termination letter.
 *
 * The banner copy describes what actually happens today: resolving every
 * dependency unblocks archiving. If scheduled deactivation is enabled (open
 * decision D1 — deactivate on the termination date rather than on submit), the
 * two resolved-state strings below become "…will auto-deactivate on {date}." and
 * this is the only place that has to change.
 */
export const ArchiveTerminationCard: React.FC<ArchiveTerminationCardProps> = ({
  personFirstName,
  personName,
  terminationDate,
  terminationReason,
  onTerminationDateChange,
  onTerminationReasonChange,
  attachment,
  attachmentUrl,
  onUpload,
  onRemoveAttachment,
  uploading,
  unresolvedItems,
  unresolvedCategories
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const acceptFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setFileError('That file is larger than 10 MB.');
      return;
    }
    setFileError(null);
    onUpload(file);
  };

  let banner = {
    tone: 'border-amber-300 bg-amber-50 text-amber-800',
    Icon: AlertTriangle,
    text: `${personName}'s Idarah account cannot be archived yet: ${unresolvedItems} unresolved ${
      unresolvedItems === 1 ? 'item' : 'items'
    } across ${unresolvedCategories} ${unresolvedCategories === 1 ? 'category' : 'categories'} below. Archiving stays
      blocked until every category is resolved.`
  };

  if (unresolvedItems === 0 && !terminationDate) {
    banner = {
      tone: 'border-gray-200 bg-slate-50 text-muted-foreground',
      Icon: Info,
      text: `All dependencies are resolved. Set a termination date to archive ${personFirstName}.`
    };
  } else if (unresolvedItems === 0 && terminationDate) {
    banner = {
      tone: 'border-green-300 bg-green-50 text-green-800',
      Icon: CheckCircle2,
      text: `All dependency categories are resolved. ${personFirstName} will be archived with a termination date of
        ${toSydneyDate(terminationDate)}.`
    };
  }

  return (
    <Card className="mb-6">
      <CardContent className="pt-6 space-y-5">
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <Label htmlFor="archive-termination-date" className="text-sm font-medium">
              Termination Date <span className="text-destructive">*</span>
            </Label>
            <Input
              id="archive-termination-date"
              type="date"
              value={terminationDate}
              onChange={(event) => onTerminationDateChange(event.target.value)}
              className="mt-1.5 h-9"
            />
          </div>

          <div>
            <Label htmlFor="archive-termination-reason" className="text-sm font-medium">
              Termination Reason <span className="text-destructive">*</span>
            </Label>
            <Select value={terminationReason} onValueChange={onTerminationReasonChange}>
              <SelectTrigger id="archive-termination-reason" className="mt-1.5 h-9">
                <SelectValue placeholder="Select a reason…" />
              </SelectTrigger>
              <SelectContent>
                {TERMINATION_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {reason}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className={cn('flex items-start gap-2 rounded-lg border px-3.5 py-3', banner.tone)}>
          <banner.Icon size={16} className="mt-0.5 flex-shrink-0" />
          <p className="text-[13px] leading-snug">{banner.text}</p>
        </div>

        <div>
          <div className="text-[13px] font-medium mb-1.5">
            Attachment <span className="font-normal text-muted-foreground">— termination letter or supporting documentation (optional)</span>
          </div>

          {!attachment && (
            <>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragActive(false);
                  acceptFile(event.dataTransfer.files?.[0]);
                }}
                disabled={uploading}
                className={cn(
                  'w-full flex items-center justify-center gap-2.5 rounded-lg border border-dashed px-5 py-6 text-[13px] text-muted-foreground transition-colors',
                  dragActive ? 'border-primary bg-accent' : 'border-input bg-slate-50 hover:bg-accent'
                )}
              >
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <UploadCloud size={18} />}
                <span>
                  <span className="font-medium text-foreground">Click to upload</span> or drag and drop · PDF, DOC, PNG up to 10 MB
                </span>
              </button>
              {fileError && <p className="mt-1.5 text-xs text-destructive">{fileError}</p>}
            </>
          )}

          {attachment && (
            <div className="flex items-center gap-3 rounded-lg border bg-background px-3.5 py-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <FileText size={18} />
              </div>
              <div className="min-w-0 flex-1">
                {attachmentUrl ? (
                  <a
                    href={attachmentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-[13px] font-medium text-primary hover:underline"
                  >
                    {attachment.name}
                  </a>
                ) : (
                  <div className="truncate text-[13px] font-medium">{attachment.name}</div>
                )}
                <div className="text-xs text-muted-foreground">{Math.max(1, Math.round(attachment.size / 1024))} KB</div>
              </div>
              <button
                type="button"
                onClick={onRemoveAttachment}
                disabled={uploading}
                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border text-destructive transition-colors hover:bg-red-50"
                title="Remove attachment"
              >
                <X size={16} />
              </button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            className="hidden"
            onChange={(event) => {
              acceptFile(event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
};
